import { Component, Input } from '@angular/core';

/**
 * Semantic tone of the badge. Mirrors the Volta spec's status-badge table;
 * colours are resolved from `$status-badge-palette` in `styles/ui/_colors.scss`.
 *
 *   • draft       – neutral grey, for not-yet-live states
 *   • success     – confirmed / active / published
 *   • alert       – awaiting an action (to be validated, quote requested)
 *   • info        – in-flight, non-blocking (quote sent)
 *   • destructive – cancelled / rejected
 *   • volta       – purple fill, product-branded states
 *   • undefined   – unknown state, faded text
 */
export type StatusBadgeTone =
  | 'draft'
  | 'success'
  | 'alert'
  | 'info'
  | 'destructive'
  | 'volta'
  | 'undefined';

/**
 * Pill rendering a lifecycle state (Published, Draft, Cancelled, …).
 *
 * `small` is the table/list variant, `big` the one used in breadcrumbs and
 * detail headers. Height is fixed per size so a badge can never reflow the row
 * it sits in.
 *
 * The component is deliberately dumb: it takes an already-translated `label`
 * and a resolved `tone`. Mapping a domain status onto a tone belongs to the
 * feature (see `features/produits/utils/product-status.ts`), so one primitive
 * can serve orders, products, workflows and catalogs without knowing any of
 * their vocabularies.
 */
@Component({
  selector: 'ui-status-badge',
  standalone: true,
  templateUrl: './status-badge.component.html',
  styleUrls: ['./status-badge.component.scss'],
})
export class StatusBadgeComponent {
  /** Already-translated text to display. */
  @Input() label = '';
  @Input() tone: StatusBadgeTone = 'undefined';
  @Input() size: 'small' | 'big' = 'small';
  /**
   * Fades the badge for rows that inherit their state from a parent rather
   * than owning it (e.g. product variants under a product).
   */
  @Input() muted = false;
  @Input() tooltip = '';

  get hostClasses(): string {
    return [
      'status-badge',
      `status-badge--${this.size}`,
      `status-badge--tone-${this.tone}`,
      this.muted ? 'status-badge--muted' : '',
    ]
      .filter(Boolean)
      .join(' ');
  }
}
